class CustomContactForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.validationAttempted = false;
    this.filesArray = [];

    this.onSubmit = this.onSubmit.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onInvalid = this.onInvalid.bind(this);
  }

  connectedCallback() {
    if (!this.form) return;

    /*
     * Deaktiviert lediglich die automatische Validierung beim Submit.
     * checkValidity() und reportValidity() funktionieren weiterhin.
     *
     * Dadurch läuft unser Submit-Handler auch bei ungültigen Feldern
     * zuverlässig und kann die eigenen CSS-Klassen setzen.
     */
    this.form.noValidate = true;
    this.validationSummary = this.querySelector('.contact-form__validation-summary');

    this.form.addEventListener('submit', this.onSubmit);
    this.form.addEventListener('input', this.onInput);
    this.form.addEventListener('change', this.onChange);
    this.form.addEventListener('focusout', this.onFocusOut);

    /*
     * Das invalid-Event bubbelt nicht.
     * Mit Capture kann es trotzdem zentral am Formular behandelt werden.
     */
    this.form.addEventListener('invalid', this.onInvalid, true);
  }

  disconnectedCallback() {
    if (!this.form) return;

    this.form.removeEventListener('submit', this.onSubmit);
    this.form.removeEventListener('input', this.onInput);
    this.form.removeEventListener('change', this.onChange);
    this.form.removeEventListener('focusout', this.onFocusOut);
    this.form.removeEventListener('invalid', this.onInvalid, true);
  }

  /**
   * Liefert alle validierbaren Formularfelder.
   *
   * @returns {(HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)[]}
   */
  getFields() {
    if (!this.form) return [];

    return Array.from(
      this.form.querySelectorAll(
        'input:not([type="hidden"]), textarea, select'
      )
    ).filter(
      (field) =>
        field instanceof HTMLInputElement ||
        field instanceof HTMLTextAreaElement ||
        field instanceof HTMLSelectElement
    );
  }

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null}
   */
  getFieldFromTarget(target) {
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement
    ) {
      return target;
    }

    return null;
  }

  /**
   * @param {SubmitEvent} event
   */
  async onSubmit(event) {
    event.preventDefault();

    if (!this.form || this.classList.contains('loading')) {
      return;
    }

    this.validationAttempted = true;

    const fields = this.getFields();

    fields.forEach((field) => {
      this.validateField(field);
    });

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      this.showValidationSummary();

      const firstInvalidField = fields.find(
        (field) => !field.validity.valid
      );

      firstInvalidField?.focus();
      return;
    }
    this.hideValidationSummary();
    await this.onSubmitHandler();
  }

  showValidationSummary() {
    if (this.validationSummary instanceof HTMLElement) {
      this.validationSummary.classList.remove('hidden');
      this.validationSummary.setAttribute('aria-hidden', 'false');
    }
  }

  hideValidationSummary() {
    if (this.validationSummary instanceof HTMLElement) {
      this.validationSummary.classList.add('hidden');
      this.validationSummary.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * @param {Event} event
   */
  onInput(event) {
    const field = this.getFieldFromTarget(event.target);

    if (!field || !this.validationAttempted) {
      return;
    }

    /*
     * Datei-Inputs werden über change verarbeitet.
     */
    if (
      field instanceof HTMLInputElement &&
      field.type === 'file'
    ) {
      return;
    }

    this.validateField(field);
  }

  /**
   * @param {Event} event
   */
  onChange(event) {
    const field = this.getFieldFromTarget(event.target);

    if (!field) return;

    if (
      field instanceof HTMLInputElement &&
      field.type === 'file'
    ) {
      this.handleFileChange(field);
    }

    if (this.validationAttempted) {
      this.validateField(field);
    }
  }

  /**
   * @param {FocusEvent} event
   */
  onFocusOut(event) {
    const field = this.getFieldFromTarget(event.target);

    if (!field || !this.validationAttempted) {
      return;
    }

    this.validateField(field);
  }

  /**
   * @param {Event} event
   */
  onInvalid(event) {
    const field = this.getFieldFromTarget(event.target);

    if (!field) return;

    this.validationAttempted = true;
    this.validateField(field);
  }

  /**
   * Prüft ein Feld anhand der nativen HTML-Regeln:
   * required, type, pattern, min, max usw.
   *
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
   * @returns {boolean}
   */
  validateField(field) {
    const isValid = field.validity.valid;

    if (
      field instanceof HTMLInputElement &&
      (field.type === 'checkbox' || field.type === 'radio')
    ) {
      this.updateCheckboxState(field, isValid);
      return isValid;
    }

    const hasValue = this.fieldHasValue(field);
    const shouldShowValid = field.required || hasValue;

    this.updateFieldState(
      field,
      isValid,
      shouldShowValid
    );

    return isValid;
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
   * @returns {boolean}
   */
  fieldHasValue(field) {
    if (
      field instanceof HTMLInputElement &&
      field.type === 'file'
    ) {
      return field.files !== null && field.files.length > 0;
    }

    if (
      field instanceof HTMLSelectElement &&
      field.multiple
    ) {
      return field.selectedOptions.length > 0;
    }

    return field.value.trim() !== '';
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
   * @param {boolean} isValid
   * @param {boolean} shouldShowValid
   */
  updateFieldState(field, isValid, shouldShowValid) {
    const wrapper = field.closest(
      '.contact-form__input-wrapper'
    );

    if (!(wrapper instanceof HTMLElement)) {
      this.updateAriaInvalid(field, isValid);
      return;
    }

    wrapper.classList.toggle(
      'contact-form__input-wrapper--valid',
      isValid && shouldShowValid
    );

    wrapper.classList.toggle(
      'contact-form__input-wrapper--invalid',
      !isValid
    );

    this.updateAriaInvalid(field, isValid);

    if (!shouldShowValid) {
      this.clearValidationIcon(wrapper);
      return;
    }

    this.setValidationIcon(
      wrapper,
      isValid ? 'checkmark' : 'error'
    );
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
   * @param {boolean} isValid
   */
  updateCheckboxState(field, isValid) {
    const wrapper = field.closest(
      '.contact-form__checkbox-wrapper'
    );

    if (!(wrapper instanceof HTMLElement)) {
      this.updateAriaInvalid(field, isValid);
      return;
    }

    const shouldShowValid =
      field.required ||
      (
        field instanceof HTMLInputElement &&
        field.checked
      );

    wrapper.classList.toggle(
      'contact-form__checkbox-wrapper--valid',
      isValid && shouldShowValid
    );

    wrapper.classList.toggle(
      'contact-form__checkbox-wrapper--invalid',
      !isValid
    );

    this.updateAriaInvalid(field, isValid);
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
   * @param {boolean} isValid
   */
  updateAriaInvalid(field, isValid) {
    if (isValid) {
      field.removeAttribute('aria-invalid');
      return;
    }

    field.setAttribute('aria-invalid', 'true');
  }

  /**
   * @param {HTMLElement} wrapper
   */
  clearValidationIcon(wrapper) {
    const iconElement = wrapper.querySelector(
      '.contact-form__validation-icon'
    );

    if (iconElement instanceof HTMLElement) {
      iconElement.innerHTML = '';
    }
  }

  /**
   * @param {HTMLElement} wrapper
   * @param {'checkmark' | 'error'} iconType
   */
  setValidationIcon(wrapper, iconType) {
    const iconElement = wrapper.querySelector(
      '.contact-form__validation-icon'
    );

    if (!(iconElement instanceof HTMLElement)) return;

    if (iconType === 'checkmark') {
      iconElement.innerHTML = `
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="icon-checkmark"
          aria-hidden="true"
        >
          <path
            d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658"
            stroke="currentColor"
            stroke-width="var(--icon-stroke-width)"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      `;

      return;
    }

    iconElement.innerHTML = `
      <svg
        class="icon icon-error"
        viewBox="0 0 13 13"
        aria-hidden="true"
      >
        <circle
          cx="6.5"
          cy="6.5"
          r="5.5"
          fill="#EB001B"
          stroke="#EB001B"
          stroke-width=".7"
        />
        <path
          fill="#fff"
          d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"
        />
      </svg>
    `;
  }

  /**
   * Fügt neu ausgewählte Dateien zur Versandliste hinzu.
   *
   * @param {HTMLInputElement} input
   */
  handleFileChange(input) {
    if (!input.files) return;

    Array.from(input.files).forEach((file) => {
      const alreadyAdded = this.filesArray.some(
        (existingFile) =>
          existingFile.name === file.name &&
          existingFile.size === file.size &&
          existingFile.lastModified === file.lastModified
      );

      if (!alreadyAdded) {
        this.filesArray.push(file);
      }
    });

    this.renderUploadedFiles();
  }

  renderUploadedFiles() {
    const container = this.querySelector('.uploaded-files');

    if (!(container instanceof HTMLElement)) return;

    container.innerHTML = '';

    this.filesArray.forEach((file) => {
      const fileElement = document.createElement('p');
      fileElement.textContent = file.name;
      container.appendChild(fileElement);
    });
  }

  /**
   * Erstellt die Datenstruktur für den bestehenden Mail-Endpunkt.
   *
   * @returns {FormData}
   */
  createFormData() {
    const finalFormData = new FormData();

    const recipientEmail = this.querySelector(
      '.form-contact-mail'
    )?.dataset.label?.trim();

    const configuredSubject = this.querySelector(
      '.form-contact-mail-topic'
    )?.dataset.label?.trim();

    /** @type {ContactFormRequestBody} */
    const requestBody = {
      subject: configuredSubject || 'Neues Formular',
      cc: [],
      inputs: [],
      recipient: ""
    };

    if (recipientEmail) {
      requestBody.recipient = recipientEmail;
    }

    const formContent = this.querySelectorAll(
      '[data-label]:not(.form-contact-mail):not(.form-contact-mail-topic)'
    );

    formContent.forEach((element) => {
      const label = element
        .getAttribute('data-label')
        ?.trim()
        .replace(/\*$/, '')
        .trim();

      if (!label) return;

      /*
       * Reguläre Input-, Textarea- und Select-Felder.
       */
      if (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement
      ) {
        if (
          element instanceof HTMLInputElement &&
          element.type === 'file'
        ) {
          return;
        }

        if (
          element instanceof HTMLInputElement &&
          (element.type === 'checkbox' || element.type === 'radio')
        ) {
          if (!element.checked) return;

          requestBody.inputs.push({
            labelName: label,
            input: element.value || 'Ja',
          });

          return;
        }

        let value = '';

        if (
          element instanceof HTMLSelectElement &&
          element.multiple
        ) {
          value = Array.from(element.selectedOptions)
            .map((option) => option.textContent?.trim() || option.value)
            .filter(Boolean)
            .join(', ');
        } else {
          value = element.value.trim();
        }

        if (!value) return;

        requestBody.inputs.push({
          labelName: label,
          input: value,
        });

        return;
      }

      /*
       * Unterstützt bei Bedarf weitere Elemente mit data-label,
       * beispielsweise statische oder editierbare Inhalte.
       */
      if (element instanceof HTMLElement) {
        const value =
          element.textContent?.trim() ||
          element.innerHTML.trim();

        if (!value) return;

        requestBody.inputs.push({
          labelName: label,
          input: value,
        });
      }
    });

    finalFormData.append(
      'requestBody',
      JSON.stringify(requestBody)
    );

    this.filesArray.forEach((file) => {
      finalFormData.append(
        'files[]',
        file,
        file.name
      );
    });

    return finalFormData;
  }

  async onSubmitHandler() {
    this.setLoadingState(true);
    this.hideMessages();

    try {
      const formData = this.createFormData();

      const response = await fetch(
        '/apps/no-cosmetics/send_form_mail',
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(
          `Form submission failed with status ${response.status}`
        );
      }

      this.showSuccess();
    } catch (error) {
      this.showError();

      console.error(
        'Error submitting contact form:',
        error
      );
    } finally {
      this.setLoadingState(false);
    }
  }

  /**
   * @param {boolean} isLoading
   */
  setLoadingState(isLoading) {
    this.classList.toggle('loading', isLoading);

    if (!this.form) return;

    this.form
      .querySelectorAll('button[type="submit"], input[type="submit"]')
      .forEach((button) => {
        if (
          button instanceof HTMLButtonElement ||
          button instanceof HTMLInputElement
        ) {
          button.disabled = isLoading;
          button.setAttribute(
            'aria-busy',
            String(isLoading)
          );
        }
      });
  }

  hideMessages() {
    const errorMessage = this.querySelector(
      '.form__message.form-error'
    );

    const successMessage = this.querySelector(
      '.form__message.form-success'
    );

    [errorMessage, successMessage].forEach((message) => {
      if (!(message instanceof HTMLElement)) return;

      message.classList.add('hidden');
      message.setAttribute('aria-hidden', 'true');
      message.removeAttribute('role');
    });
  }

  showSuccess() {
    const errorMessage = this.querySelector(
      '.form__message.form-error'
    );

    const successMessage = this.querySelector(
      '.form__message.form-success'
    );

    if (errorMessage instanceof HTMLElement) {
      errorMessage.classList.add('hidden');
      errorMessage.setAttribute('aria-hidden', 'true');
      errorMessage.removeAttribute('role');
    }

    if (successMessage instanceof HTMLElement) {
      successMessage.classList.remove('hidden');
      successMessage.setAttribute('aria-hidden', 'false');
      successMessage.setAttribute('role', 'status');
      successMessage.setAttribute('tabindex', '-1');
      successMessage.focus();
    }

    const wrapper = this.querySelector(
      '.custom-form-mail-wrapper'
    );

    if (wrapper instanceof HTMLElement) {
      wrapper.classList.add('hidden');
      wrapper.setAttribute('aria-hidden', 'true');
    }
  }

  showError() {
    const successMessage = this.querySelector(
      '.form__message.form-success'
    );

    const errorMessage = this.querySelector(
      '.form__message.form-error'
    );

    if (successMessage instanceof HTMLElement) {
      successMessage.classList.add('hidden');
      successMessage.setAttribute('aria-hidden', 'true');
      successMessage.removeAttribute('role');
    }

    if (errorMessage instanceof HTMLElement) {
      errorMessage.classList.remove('hidden');
      errorMessage.setAttribute('aria-hidden', 'false');
      errorMessage.setAttribute('role', 'alert');
      errorMessage.setAttribute('tabindex', '-1');
      errorMessage.focus();
    }
  }
}

if (!customElements.get('custom-contact-form')) {
  customElements.define(
    'custom-contact-form',
    CustomContactForm
  );
}